import { useRef } from 'react';
import { Link } from 'react-router';

import BetaBadge from '@cartwhl/ui/atoms/BetaBadge';
import Text from '@cartwhl/ui/base/TextNew';

import type { DashboardAppletCardData } from '../types';

export interface DashboardAppletCardProps {
  applet: DashboardAppletCardData;
}

export default function DashboardAppletCard({
  applet,
}: DashboardAppletCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardClassName = `group flex min-h-[116px] gap-4 overflow-hidden rounded-2xl border border-primary bg-primary p-2 pr-4 transition-colors sm:gap-5 ${
    applet.disabled ? 'opacity-60' : 'hover:bg-secondary'
  }`;

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    void video.play().catch(() => undefined);
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
  };

  const cardInteractionProps = applet.videoSrc
    ? {
        onBlur: stopPreview,
        onFocus: playPreview,
        onMouseEnter: playPreview,
        onMouseLeave: stopPreview,
      }
    : undefined;
  const content = (
    <>
      <div className="bg-tertiary h-[100px] w-[100px] flex-none overflow-hidden rounded-lg max-[340px]:size-[72px] max-[340px]:self-center">
        {applet.videoSrc ? (
          <video
            ref={videoRef}
            src={applet.videoSrc}
            poster={applet.imageSrc}
            className="h-full w-full object-cover"
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
          />
        ) : applet.imageSrc ? (
          <img
            src={applet.imageSrc}
            alt={applet.imageAlt ?? ''}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="flex h-5 min-w-0 items-center gap-2">
          <Text
            token="14/medium"
            leading="title"
            className="text-primary min-w-0 flex-1 truncate"
          >
            {applet.title}
          </Text>
          {applet.badge ? (
            <div className="absolute -right-2.5 top-1">
              <BetaBadge className="ml-auto" label={applet.badge} />
            </div>
          ) : null}
        </div>

        {applet.description ? (
          <Text token="14/medium" className="text-tertiary h-13 line-clamp-3">
            {applet.description}
          </Text>
        ) : null}
      </div>
    </>
  );

  if (applet.disabled) {
    return (
      <div
        aria-disabled="true"
        className={cardClassName}
        {...cardInteractionProps}
      >
        {content}
      </div>
    );
  }

  if (applet.external) {
    return (
      <a href={applet.path} className={cardClassName} {...cardInteractionProps}>
        {content}
      </a>
    );
  }

  return (
    <Link to={applet.path} className={cardClassName} {...cardInteractionProps}>
      {content}
    </Link>
  );
}
